import _ from "lodash";
import {
  getUpdateData,
  persistAddress,
  persistAppointment,
  persistCourt,
  persistCustomer,
  persistCaseInsurance,
  persistPerson,
  sanitizeOrganizationEmailData,
} from "./persisterService";
import { apiPatch } from "../Api/apiCall";
import { DivorceChild } from "../../types/DivorceChild";
import { CaseInsurance } from "../../types/CaseInsurance";
import { UseForm } from "../../types/UseForm";
import { Divorce } from "../../types/Products/FamilyLaw/Divorce";
import ApiClient from "../ApiClient";

export const persistDivorce = async (updateData: UseForm["values"], origDivorce: Divorce) => {
  const subEntityRequests = [];

  if (_.isObject(updateData.customer)) {
    const customerUpdateData = getUpdateData(updateData.customer, origDivorce, "customer");
    subEntityRequests.push(() => persistCustomer(origDivorce.customer.id, customerUpdateData));
  }

  //this is divorce specific courtDate
  if (_.isObject(updateData.courtDate)) {
    const courtDateUpdateData = getUpdateData(updateData.courtDate, origDivorce, "courtDate");
    subEntityRequests.push(() => persistAppointment(origDivorce.courtDate.id, courtDateUpdateData));
  }

  if (_.isObject(updateData.petitioner)) {
    const petitionerUpdateData = getUpdateData(updateData.petitioner, origDivorce, "petitioner");
    subEntityRequests.push(() => persistPerson(origDivorce.petitioner.id, petitionerUpdateData));
  }

  if (origDivorce.responsibleCourt && _.isObject(updateData.responsibleCourt)) {
    const courtUpdateData = getUpdateData(updateData.responsibleCourt, origDivorce, "responsibleCourt");
    !_.isEmpty(courtUpdateData) && (await persistCourt(origDivorce.responsibleCourt.id, courtUpdateData));
  }

  if (_.isObject(updateData.opponentLawyer)) {
    const opponentLawyerUpdateData = getUpdateData(updateData.opponentLawyer, origDivorce, "opponentLawyer");
    await persistPerson(origDivorce.opponentLawyer.id, opponentLawyerUpdateData);
  }

  if (_.isObject(updateData.opponentLawFirm?.organization)) {
    const opponentLawFirmUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization,
      origDivorce,
      "opponentLawFirm.organization"
    );
    await apiPatch("organizations", origDivorce.opponentLawFirm.organization.id, opponentLawFirmUpdateData);
  }

  if (_.isObject(updateData.opponentLawFirm?.organization?.mainEmail)) {
    const opponentLawFirmUpdateEmailUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization.mainEmail,
      origDivorce,
      "opponentLawFirm.organization.mainEmail"
    );
    await ApiClient.patch(`organization_emails/${origDivorce.opponentLawFirm.organization.mainEmail.id}`, {
      body: JSON.stringify(sanitizeOrganizationEmailData(opponentLawFirmUpdateEmailUpdateData)),
    });
  }

  if (
    _.has(updateData, "opponentLawFirm.organization.address") &&
    _.isObject(updateData.opponentLawFirm.organization.address)
  ) {
    const opponentLawFirmAddressUpdateData = getUpdateData(
      updateData.opponentLawFirm.organization.address,
      origDivorce,
      "opponentLawFirm.organization.address"
    );
    await persistAddress(origDivorce.opponentLawFirm.organization.address.id, opponentLawFirmAddressUpdateData);
  }

  if (_.isObject(updateData.caseInsurance)) {
    let caseInsuranceUpdateData = getUpdateData(updateData.caseInsurance, origDivorce, "caseInsurance");

    const caseInsurance: Partial<CaseInsurance> = updateData.caseInsurance;
    caseInsuranceUpdateData.insurer = caseInsurance.insurer?.id
      ? "/insurers/" + caseInsurance.insurer.id
      : origDivorce.caseInsurance.insurer?.["@id"];

    subEntityRequests.push(() => persistCaseInsurance(origDivorce.caseInsurance.id, caseInsuranceUpdateData));
  }

  if (_.has(updateData, "caseInsurance.policyHolder") && _.isObject(updateData.caseInsurance.policyHolder)) {
    const policyHolderUpdateData = getUpdateData(
      updateData.caseInsurance.policyHolder,
      origDivorce,
      "caseInsurance.policyHolder"
    );
    await persistPerson(origDivorce.caseInsurance.policyHolder.id, policyHolderUpdateData);
  }

  if (_.has(updateData, "petitioner.residenceAddress") && _.isObject(updateData.petitioner.residenceAddress)) {
    const petitionerAddressUpdateData = getUpdateData(
      updateData.petitioner.residenceAddress,
      origDivorce,
      "petitioner.residenceAddress"
    );
    subEntityRequests.push(() =>
      persistAddress(origDivorce.petitioner.residenceAddress.id, petitionerAddressUpdateData)
    );
  }

  if (_.isObject(updateData.partner)) {
    const partnerUpdateData = getUpdateData(updateData.partner, origDivorce, "partner");
    subEntityRequests.push(() => persistPerson(origDivorce.partner.id, partnerUpdateData));
  }

  if (_.has(updateData, "partner.residenceAddress") && _.isObject(updateData.partner.residenceAddress)) {
    const partnerAddressUpdateData = getUpdateData(
      updateData.partner.residenceAddress,
      origDivorce,
      "partner.residenceAddress"
    );
    subEntityRequests.push(() => persistAddress(origDivorce.partner.residenceAddress.id, partnerAddressUpdateData));
  }

  if (_.has(updateData, "commonAddress") && _.isObject(updateData.commonAddress)) {
    const commonAddressUpdateData = getUpdateData(updateData.commonAddress, origDivorce, "commonAddress");
    subEntityRequests.push(() => persistAddress(origDivorce.commonAddress.id, commonAddressUpdateData));
  }

  if (_.has(updateData, "children")) {
    _.forEach(updateData.children, (child, index) => {
      const childUpdateData = getUpdateData(child, origDivorce, `children[${index}]`);
      const childId = origDivorce.children?.[Number(index)].id;

      if (!childId) {
        return;
      }

      subEntityRequests.push(() => persistDivorceChild(childId, childUpdateData));
    });
  }

  await Promise.all(_.map(subEntityRequests, (request) => request()));

  const divorceUpdateData = getUpdateData(updateData, origDivorce);
  await apiPatch("divorces", origDivorce.id, divorceUpdateData);
};

const persistDivorceChild = async (id: number, childData: Partial<DivorceChild>) => {
  return await apiPatch("divorce_children", id, childData);
};
